<script lang="ts">
import { defineComponent, onMounted } from 'vue'
import * as jwt from 'jsonwebtoken'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import Cookies from 'js-cookie'
import {
  COOKIES_TOKEN_KEY,
  LOCAL_STORAGE_IID_KEY,
  SESSION_FORCE_MFA_TOTP_ONLY_KEY,
  LOCAL_STORAGE_REDIRECT_URI_KEY,
  LOCAL_STORAGE_TRUSTED_DEVICE_SETUP_REDIRECT_URI_KEY,
  LOCAL_STORAGE_LAST_LOGIN_EMAIL_KEY,
  IMPOSSIBLE_TRAVEL_ERROR_MESSAGE,
} from '@/constants'
import { getAuthXToken } from '@/services/auth/authx-token-payload'
import { generateNewUserDeviceToken, getLocalUserDeviceToken } from '@/services/auth/authx-user-device-token'
import { getDeviceKey, removeDeviceKey } from '@/services/trusted-device/trusted-device-store'
import { isAllowedRedirectUri } from '@/services/trusted-device/is-allowed-redirect-uri'
import { heap, HeapEvent } from '@/setup/setup-heap'
import { loadLanguageAsync } from '@/setup/setup-i18n'
import { nrLoginPasskey, nrLoginPassword } from '@/services/newrelic'
import { NR_STEPS } from '@/services/newrelic/constants'
import { getCurrentStep } from '@/services/auth/authx-session'
import { isTrustedDeviceEnrollmentEnabled } from '@/services/trusted-device/is-trusted-device-enrollment-enabled'
import { UserState, MfaPurpose, Partner, AuthPurpose, PasskeyPurpose, TrustedDevicePurpose } from '../../types'
import { useRoute, router } from '../../router'

const {
  useActions: useUserStoreActions,
  useMutations: useUserStoreMutations,
} = createNamespacedHelpers('User')
const { useState: useLoginDomainStoreState } = createNamespacedHelpers('LoginDomain')

const extractEmailDomain = (email: string): string => {
  return email.split('@')[1]
}

export default defineComponent({

  name: 'AuthenticatePage',

setup() {
    const { authenticateAgainstAuthX, verifyPasskeyLogin, checkTrustedDeviceStatus } = useUserStoreActions(['authenticateAgainstAuthX', 'verifyPasskeyLogin', 'checkTrustedDeviceStatus'])
    const { tenant } = useLoginDomainStoreState(['tenant'])
    const { storeUserIdentity } = useUserStoreMutations(['storeUserIdentity'])

    const currentRoute = useRoute()

    const getCode = (): string => {
      return currentRoute.value?.query.code as string
    }

    const getIID = (): string => {
      return localStorage.getItem(LOCAL_STORAGE_IID_KEY) as string
    }

    const getAuthenticationPurpose = (): string => {
      return currentRoute.value?.query.purpose as string
    }

    const getAuthState = (): string => {
      return currentRoute.value?.query.state as string
    }

    // force_mfa=true with prompt=login comes from the mobile app even though iid is omitted. Read once
    // per /authenticate visit (see consumeForceMfaFlag) so it only ever applies to this login.
    let isForceMfaFromMobileApp = false

    // MfaSetupPage still needs the flag (and clears it itself on success); every other outcome
    // drops it here so a single force_mfa link cannot affect a later login in this tab
    const routesToMfaSetupPage = (state: UserState): boolean => {
      if (state === UserState.MFA_REGISTRATION_RECOMMENDED) {
        return !!currentRoute.value?.query.applyMfa
      }
      return [
        UserState.MFA_REGISTRATION_OPTIONAL,
        UserState.MFA_REGISTRATION_REQUIRED,
        UserState.MFA_VERIFICATION_REQUIRED,
      ].includes(state)
    }

    const consumeForceMfaFlag = (state?: UserState) => {
      if (!state || !routesToMfaSetupPage(state)) {
        sessionStorage.removeItem(SESSION_FORCE_MFA_TOTP_ONLY_KEY)
      }
    }

    const trustedDeviceSetup = async (userIdentity: any, trustedDeviceSetupRedirectUri?: string) => {
      const token = getAuthXToken()
      if (getIID() || isForceMfaFromMobileApp || !isTrustedDeviceEnrollmentEnabled(userIdentity.email, token?.payload.partnerCode)) {
        await router.replace({ name: 'AuthenticatedPage' })
        return
      }

      const storedRedirectUri = localStorage.getItem(LOCAL_STORAGE_REDIRECT_URI_KEY)
      if (storedRedirectUri && storedRedirectUri.includes(`purpose=${TrustedDevicePurpose.trustedDeviceEnrollment}`)) {
        localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_KEY)
      }

      // Sends the device through the trusted device challenge (rather than
      // trusting the override redirect URI outright) when the override is
      // present, clearing it either way so it can't be replayed on a later,
      // unrelated enrollment. Returns whether it redirected, so callers know
      // whether to fall through to AuthenticatedPage.
      const redirectToOverride = (): boolean => {

        if (!trustedDeviceSetupRedirectUri || !isAllowedRedirectUri(trustedDeviceSetupRedirectUri)) {
          localStorage.removeItem(LOCAL_STORAGE_TRUSTED_DEVICE_SETUP_REDIRECT_URI_KEY)
          return false
        }

        // Leave the override in localStorage — TrustedDeviceVerificationPage reads
        // (and clears) it itself once the challenge is verified.
        router.replace({ name: 'TrustedDeviceVerificationPage' })
        return true

      }

      const device = await getDeviceKey(userIdentity.email).catch(() => {
        // swallow the error, as device key is optional
        return null
      })

      if (device) {

        try {
          const result = await checkTrustedDeviceStatus({
            deviceId: device.deviceId,
            token: userIdentity.token,
          })

          if (result.status === 'pending') {
            await router.replace({
              name: 'TrustedDeviceSetupPage',
            })
          } else if ('status' in result && result.status === 'confirmed') {

            if (redirectToOverride()) {
              return
            }

            await router.replace({ name: 'AuthenticatedPage' })

          } else if ('code' in result && result.code === 'TrustedDeviceNotFound') {
            await removeDeviceKey(userIdentity.email).catch(() => null)
            await router.replace({
              name: 'TrustedDeviceSetupPage',
            })
          } else {

            await router.replace({ name: 'AuthenticatedPage' })

          }

        } catch (error: any) {
          if (error?.response?.status === 404) {
            await removeDeviceKey(userIdentity.email).catch(() => null)
            await router.replace({
              name: 'TrustedDeviceSetupPage',
            })
          } else {

            // Status check failed (5xx or any other non-404 error) — we can't
            // confirm the device is trusted, so don't send it through the
            // trusted device challenge. Clear the override so it can't be
            // replayed on a later, unrelated enrollment, and fall through to
            // the default landing page.
            localStorage.removeItem(LOCAL_STORAGE_TRUSTED_DEVICE_SETUP_REDIRECT_URI_KEY)
            await router.replace({ name: 'AuthenticatedPage' })
          }
        }

      } else {
        await router.replace({
          name: 'TrustedDeviceSetupPage',
        })
      }
    }

    // Establishes the AUTH_X_TOKEN session cookie from the identity token.
    const setSessionCookie = (userIdentity: any, authenticatePurpose: string) => {

      const tokenDecoded = jwt.decode(userIdentity.token) as { exp: number }
      const cookieAttr: Cookies.CookieAttributes = {
        expires: new Date(tokenDecoded.exp * 1000),
        secure: true,
      }

      const allowRelaxedCookies = [
        MfaPurpose.mfaVerification,
        AuthPurpose.reAuth,
      ]

      if (allowRelaxedCookies.includes(authenticatePurpose as any) && userIdentity.token) {
        const token = jwt.decode(userIdentity.token)
        if (token?.partnerCode === Partner.LittleHotelier) {
          cookieAttr.sameSite = 'None'
        }
      }

      Cookies.set(COOKIES_TOKEN_KEY, userIdentity.token, cookieAttr)

    }

    // Registers a new user device token when one isn't already held. Only reached by
    // states with a persisted session — PASSWORD_EXPIRED never calls it.
    const registerDeviceToken = async (userIdentity: any, currentUserDeviceToken?: string): Promise<string | undefined> => {

      if (currentUserDeviceToken) {
        return currentUserDeviceToken
      }

      const userDeviceToken = await generateNewUserDeviceToken()
      heap.track(HeapEvent.MfaDeviceIdGenerated)
      return userDeviceToken

    }

    // Side-effects each outcome needs before routing, keyed on state.
    const preStateRoutingActions = async (
      userIdentity: any,
      authenticatePurpose: string,
      userDeviceToken?: string,
    ): Promise<void> => {

      switch (userIdentity.state) {

      // Cannot proceed — no session cookie, no device token.
      case UserState.PASSWORD_EXPIRED:
        break

      // Establish the session and remember the device for MFA.
      case UserState.MFA_REGISTRATION_RECOMMENDED:
      case UserState.MFA_REGISTRATION_OPTIONAL:
      case UserState.MFA_REGISTRATION_REQUIRED:
      case UserState.MFA_VERIFICATION_REQUIRED:
      case UserState.TRUSTED_DEVICE_VERIFICATION_REQUIRED:
        setSessionCookie(userIdentity, authenticatePurpose)
        await registerDeviceToken(userIdentity, userDeviceToken)
        break

      // Passkey logins always resolve to AUTHENTICATED, so verify the passkey here.
      case UserState.AUTHENTICATED: {
        setSessionCookie(userIdentity, authenticatePurpose)
        const deviceToken = await registerDeviceToken(userIdentity, userDeviceToken)

        if (
          userIdentity.passkeys
          && userIdentity.passkeys.length > 0
          && authenticatePurpose === AuthPurpose.passkeyLogin
        ) {
          await verifyPasskeyLogin({
            token: userIdentity.token,
            userDeviceToken: deviceToken,
            state: currentRoute.value?.query.state,
          })
          nrLoginPasskey()
        }

        break
      }

      default:
        break

      }

    }

    // Navigates to the next screen for the outcome, keyed on state.
    const doStateRouting = async (userIdentity: any, authenticatePurpose: string): Promise<void> => {

      switch (userIdentity.state) {
      case UserState.MFA_REGISTRATION_RECOMMENDED:
        if (currentRoute.value?.query.applyMfa) {
          await router.replace({ name: 'MfaSetupPage' })
        } else {
          await router.replace({ name: 'AuthenticatedPage' })
        }
        return
      case UserState.MFA_REGISTRATION_OPTIONAL:
        // opted-in MFA setup: the OPTIONAL state itself drives the setup screen,
        // so no query is needed (MfaSetupPage keys off userIdentity.state)
        await router.replace({ name: 'MfaSetupPage' })
        return
      case UserState.MFA_REGISTRATION_REQUIRED:
        await router.replace({ name: 'MfaSetupPage' })
        return
      case UserState.MFA_VERIFICATION_REQUIRED:
        await router.replace({ name: 'MfaSetupPage' })
        return
      case UserState.TRUSTED_DEVICE_VERIFICATION_REQUIRED:
        await router.replace({ name: 'TrustedDeviceVerificationPage' })
        return
      case UserState.AUTHENTICATED: {
        if (authenticatePurpose === 'for-mfa-reset') {
          await router.replace({
            name: 'MfaResetPage',
            query: {
              redirectUri: currentRoute.value?.query.mfaResetRedirectUri as string,
            },
          })
        } else if (authenticatePurpose === MfaPurpose.mfaVerification) {
          await router.replace({
            name: 'MfaVerificationPage',
          })
        } else if (
          authenticatePurpose === PasskeyPurpose.passkeyRegistration
          || authenticatePurpose === PasskeyPurpose.passkeyEnrollment
          || authenticatePurpose === PasskeyPurpose.passkeyEnrollmentCancelled
        ) {
          await router.replace({
            name: 'PasskeyEnrollmentPage',
            query: {
              purpose: authenticatePurpose,
            },
          })
        } else if (
          authenticatePurpose === PasskeyPurpose.passkeyStepUpAuthentication
          || authenticatePurpose === PasskeyPurpose.passkeyStepUpSuccess
          || authenticatePurpose === PasskeyPurpose.passkeyStepUpCancelled
        ) {
          await router.replace({
            name: 'PasskeyStepUpPage',
            query: {
              purpose: authenticatePurpose,
              state: getAuthState(),
            },
          })
        } else if (authenticatePurpose === AuthPurpose.passkeyLogin && userIdentity.passkeys && userIdentity.passkeys.length > 0) {
          await trustedDeviceSetup(userIdentity)
        } else if (authenticatePurpose === TrustedDevicePurpose.trustedDeviceEnrollment) {
          const trustedDeviceSetupRedirectUri = localStorage.getItem(LOCAL_STORAGE_TRUSTED_DEVICE_SETUP_REDIRECT_URI_KEY) ?? undefined
          await trustedDeviceSetup(userIdentity, trustedDeviceSetupRedirectUri)
        } else {
          await router.replace({ name: 'AuthenticatedPage' })
        }
        return
      }
      case UserState.PASSWORD_EXPIRED:
        // Password expired — the user must reset it before continuing.
        await router.push({ name: 'PasswordExpiredErrorPage' })
        return
      default:
        throw new Error('UnknownError')
      }

    }

    const authenticate = async () => {

      let isPassportTravelError = false

      // the state this visit routes on; undefined for every early exit (error page, logout)
      let routedState: UserState | undefined

      isForceMfaFromMobileApp = sessionStorage.getItem(SESSION_FORCE_MFA_TOTP_ONLY_KEY) === 'true'

      try {

        if (currentRoute.value?.query.error_description === IMPOSSIBLE_TRAVEL_ERROR_MESSAGE) {
          isPassportTravelError = true
        }

        if (!currentRoute.value?.query.code || !getAuthState()) {
          throw new Error('Bad request')
        }

        const userDeviceToken = getLocalUserDeviceToken()

        const lastLoginEmail = localStorage.getItem(LOCAL_STORAGE_LAST_LOGIN_EMAIL_KEY)

        let device

        if (lastLoginEmail) {

          device = await getDeviceKey(lastLoginEmail).catch(() => {
            // swallow the error, as device key is optional
            return null
          })

          localStorage.removeItem(LOCAL_STORAGE_LAST_LOGIN_EMAIL_KEY)

        }

        const deviceId = device?.deviceId || undefined

        const authenticatePurpose = getAuthenticationPurpose()
        const temporaryMfaSetup = !!currentRoute.value?.query.temporaryMfaSetup

        const userIdentity = await authenticateAgainstAuthX({
          code: getCode(),
          tenantCode: tenant.value?.code,
          userDeviceToken: userDeviceToken || undefined, // swagger doesn't accept null value even I have put nullable: true
          iid: getIID(),
          state: authenticatePurpose === AuthPurpose.passkeyLogin
            ? currentRoute.value?.query.state as string
            : undefined,
          deviceId,
          // only sent when set: lets the backend offer optional MFA registration even though
          // the user is not MFA-required (temporary MFA setup triggered from My Account)
          ...(temporaryMfaSetup ? { temporaryMfaSetup: true } : {}),
        })

        const authxToken = getAuthXToken()

        if (getAuthenticationPurpose() === PasskeyPurpose.passkeyStepUpSuccess && authxToken?.payload?.email) {
          if (userIdentity.email !== authxToken.payload.email) {
            router.replace({ name: 'LogoutPage' })
            return
          }
        }

        storeUserIdentity(userIdentity)

        heap.identify(userIdentity.uuid)
        heap.addUserProperties({
          authx_user_id: userIdentity.uuid,
          email_domain: extractEmailDomain(userIdentity.email),
          user_type: userIdentity.type,
        })

        // load user chosen locale
        if (userIdentity.locale) {
          loadLanguageAsync(userIdentity.locale)
        }

        const step = getCurrentStep()

        await preStateRoutingActions(userIdentity, authenticatePurpose, userDeviceToken)

        // Must run after the cookie is written — the staff exclusion reads AUTH_X_TOKEN.
        if (step && step === NR_STEPS.LOGIN_EMAIL) {
          nrLoginPassword()
        }

        routedState = userIdentity.state

        await doStateRouting(userIdentity, authenticatePurpose)

      } catch (error) {
        routedState = undefined
        await router.replace({
          name: 'UnknownErrorPage',
          ...(isPassportTravelError ? {
            query: {
              errorMessage: currentRoute.value?.query.error_description,
            },
          } : {}),
        })
      } finally {
        consumeForceMfaFlag(routedState)
      }

    }

    onMounted(async () => {
      await authenticate()
    })

    return {
      tenant,
      authenticateAgainstAuthX,
      code: getCode(),
    }

  },

})

</script>

<template>
  <div class="authenticate">
    <sm-loader />
  </div>
</template>

<style lang="scss" scoped>
.authenticate {
  @apply flex justify-center items-center min-h-screen;
}
</style>
